<script setup lang="ts">
/**
 * Rein präsentatives Badge für den Zustand der automatischen Akkorderkennung.
 *
 * Die vier Zustände (`never`/`active`/`empty`/`removed`) leitet `chordInferenceStatus`
 * (`@/composables/musicXmlDom`) inhaltsbasiert aus dem MusicXML ab; Label/Farbe/Icon kommen aus
 * der zentralen Tabelle `CHORD_INFERENCE_DISPLAY`. Genutzt vom Akkorderkennungs-Panel, der
 * Notenvorschau und dem Verlauf – so gibt es genau **eine** Definition der Darstellung.
 */
import {computed} from 'vue';
import {Icon} from '@iconify/vue';
import type {ChordInferenceStatus} from '@/composables/musicXmlDom';
import {CHORD_INFERENCE_DISPLAY, formatChordInferenceText} from './chordInferenceDisplay';

const {status, variant = 'full'} = defineProps<{
  status: ChordInferenceStatus;
  /** `compact` kürzt Label und Innenabstand für enge Kontexte (Toolbar, Verlaufs-Eintrag). */
  variant?: 'full' | 'compact';
}>();

const display = computed(() => CHORD_INFERENCE_DISPLAY[status.state]);
const label = computed(() =>
    formatChordInferenceText(
        variant === 'compact' ? display.value.shortLabel : display.value.label,
        status.count,
    ),
);
const title = computed(() => formatChordInferenceText(display.value.title, status.count));
</script>

<template>
  <div
      class="inline-flex items-center gap-1.5 rounded-full border font-medium animate-fade-in"
      :class="[display.classes, variant === 'compact' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs']"
      :title="title"
  >
    <Icon v-if="display.icon" :icon="display.icon" class="shrink-0"/>
    <span
        v-else-if="display.dotColor"
        class="inline-block w-2 h-2 rounded-full shrink-0"
        :style="{background: display.dotColor}"
    ></span>
    <span class="truncate">{{ label }}</span>
  </div>
</template>
